import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import '@/App.css';
import '@/redesign.css';
import { ThemeProvider } from '@/contexts/ThemeContext';
import Sidebar from '@/components/layout/Sidebar';
import Navbar from '@/components/layout/Navbar';








import CookieConsent from '@/components/shared/CookieConsent';
import { blogPostBySlug } from '@/data/blogPosts';
import { legalDocs } from '@/data/legalContent';

import SEO from '@/components/shared/SEO';

import GlobeLogo from '@/components/shared/GlobeLogo';
import { initConsentMode, grantAnalyticsConsent, trackPageView } from '@/lib/analytics';
import { hasAcceptedCookies } from '@/components/shared/CookieConsent';
import { calculatorCategories, getCalculatorById, getCategoryById } from '@/data/calculatorRegistry';
import { SLUG_BY_ID, ID_BY_SLUG, slugFor, idFromSlug, categorySlugFor, categoryIdFromSlug } from '@/data/slugs';
import { getSeoForCalc } from '@/data/seo';
import { Wallet, Building2, Heart, Menu, Home } from 'lucide-react';

// ── Lazy-loaded calculator chunks (code splitting) ──
const lazy = (importFn, name) => React.lazy(() => importFn().then(m => ({ default: m[name] })));
// ── Lazy-loaded page components ──
const HomePage        = React.lazy(() => import('@/pages/HomePage'));
const AboutPage       = React.lazy(() => import('@/pages/AboutPage'));
const CategoryPage    = React.lazy(() => import('@/pages/CategoryPage'));
const ContactPage     = React.lazy(() => import('@/pages/ContactPage'));
const SitemapPage     = React.lazy(() => import('@/pages/SitemapPage'));
const BlogListPage    = React.lazy(() => import('@/pages/BlogListPage'));
const BlogPostPage    = React.lazy(() => import('@/pages/BlogPostPage'));
const LegalPage       = React.lazy(() => import('@/pages/LegalPage'));
const HistoryPanel    = React.lazy(() => import('@/components/shared/HistoryPanel'));
const CalculatorContent = React.lazy(() => import('@/components/shared/CalculatorContent'));


const ScientificCalculator = lazy(() => import('@/calculators/ScientificCalculator'), 'ScientificCalculator');
const EquationSolver = lazy(() => import('@/calculators/MathCalculators'), 'EquationSolver');
const MatrixCalculator = lazy(() => import('@/calculators/MathCalculators'), 'MatrixCalculator');
const ComplexNumberCalc = lazy(() => import('@/calculators/MathCalculators'), 'ComplexNumberCalc');
const BaseConverter = lazy(() => import('@/calculators/MathCalculators'), 'BaseConverter');
const PrimeFactorization = lazy(() => import('@/calculators/MathCalculators'), 'PrimeFactorization');
const DerivativeCalc = lazy(() => import('@/calculators/CalculusCalculators'), 'DerivativeCalc');
const IntegralCalc = lazy(() => import('@/calculators/CalculusCalculators'), 'IntegralCalc');
const LimitCalc = lazy(() => import('@/calculators/CalculusCalculators'), 'LimitCalc');
const SeriesCalc = lazy(() => import('@/calculators/CalculusCalculators'), 'SeriesCalc');
const FunctionGrapher = lazy(() => import('@/calculators/CalculusCalculators'), 'FunctionGrapher');
const KinematicsCalc = lazy(() => import('@/calculators/PhysicsCalculators'), 'KinematicsCalc');
const NewtonsLawsCalc = lazy(() => import('@/calculators/PhysicsCalculators'), 'NewtonsLawsCalc');
const EnergyWorkCalc = lazy(() => import('@/calculators/PhysicsCalculators'), 'EnergyWorkCalc');
const ElectromagnetismCalc = lazy(() => import('@/calculators/PhysicsCalculators'), 'ElectromagnetismCalc');
const OpticsCalc = lazy(() => import('@/calculators/PhysicsCalculators'), 'OpticsCalc');
const ThermodynamicsCalc = lazy(() => import('@/calculators/PhysicsCalculators'), 'ThermodynamicsCalc');
const WavesSoundCalc = lazy(() => import('@/calculators/PhysicsCalculators'), 'WavesSoundCalc');
const RelativityCalc = lazy(() => import('@/calculators/PhysicsCalculators'), 'RelativityCalc');
const MolarMassCalc = lazy(() => import('@/calculators/ChemistryCalculators'), 'MolarMassCalc');
const StoichiometryCalc = lazy(() => import('@/calculators/ChemistryCalculators'), 'StoichiometryCalc');
const PhTitrationCalc = lazy(() => import('@/calculators/ChemistryCalculators'), 'PhTitrationCalc');
const GasLawsCalc = lazy(() => import('@/calculators/ChemistryCalculators'), 'GasLawsCalc');
const ElectrochemistryCalc = lazy(() => import('@/calculators/ChemistryCalculators'), 'ElectrochemistryCalc');
const NuclearCalc = lazy(() => import('@/calculators/ChemistryCalculators'), 'NuclearCalc');
const DescriptiveStatsCalc = lazy(() => import('@/calculators/StatisticsCalculators'), 'DescriptiveStatsCalc');
const ProbabilityCalc = lazy(() => import('@/calculators/StatisticsCalculators'), 'ProbabilityCalc');
const HypothesisTestingCalc = lazy(() => import('@/calculators/StatisticsCalculators'), 'HypothesisTestingCalc');
const RegressionCalc = lazy(() => import('@/calculators/StatisticsCalculators'), 'RegressionCalc');
const DistributionTablesCalc = lazy(() => import('@/calculators/StatisticsCalculators'), 'DistributionTablesCalc');
const UnitConverterFull = lazy(() => import('@/calculators/UnitConverterFull'), 'UnitConverterFull');
const SigFigsCalc = lazy(() => import('@/calculators/EngineeringCalculators'), 'SigFigsCalc');
const VectorCalc = lazy(() => import('@/calculators/EngineeringCalculators'), 'VectorCalc');
const FluidMechanicsCalc = lazy(() => import('@/calculators/EngineeringCalculators'), 'FluidMechanicsCalc');
const ElectricalCircuitsCalc = lazy(() => import('@/calculators/EngineeringCalculators'), 'ElectricalCircuitsCalc');
const GeneticsCalc = lazy(() => import('@/calculators/BiologyCalculators'), 'GeneticsCalc');
const PopulationGrowthCalc = lazy(() => import('@/calculators/BiologyCalculators'), 'PopulationGrowthCalc');
const BodyMetricsCalc = lazy(() => import('@/calculators/BiologyCalculators'), 'BodyMetricsCalc');
const AstronomyCalc = lazy(() => import('@/calculators/BiologyCalculators'), 'AstronomyCalc');
const GeoscienceCalc = lazy(() => import('@/calculators/BiologyCalculators'), 'GeoscienceCalc');

// Finance calculators
const EMICalculator = lazy(() => import('@/calculators/FinanceCalculators'), 'EMICalculator');
const MortgageCalculator = lazy(() => import('@/calculators/FinanceCalculators'), 'MortgageCalculator');
const CompoundInterestCalc = lazy(() => import('@/calculators/FinanceCalculators'), 'CompoundInterestCalc');
const SIPCalculator = lazy(() => import('@/calculators/FinanceCalculators'), 'SIPCalculator');
const GSTCalculator = lazy(() => import('@/calculators/FinanceCalculators'), 'GSTCalculator');
const SalaryCalculator = lazy(() => import('@/calculators/FinanceCalculators'), 'SalaryCalculator');
const DiscountCalculator = lazy(() => import('@/calculators/FinanceCalculators'), 'DiscountCalculator');
const ProfitLossCalc = lazy(() => import('@/calculators/FinanceCalculators'), 'ProfitLossCalc');
const ROICalculator = lazy(() => import('@/calculators/FinanceCalculators'), 'ROICalculator');
const TipCalculator = lazy(() => import('@/calculators/FinanceCalculators'), 'TipCalculator');
const PPFCalculator = lazy(() => import('@/calculators/FinanceCalculators'), 'PPFCalculator');
const InflationCalculator = lazy(() => import('@/calculators/FinanceCalculators'), 'InflationCalculator');

// Construction calculators
const ConcreteCalculator = lazy(() => import('@/calculators/ConstructionCalculators'), 'ConcreteCalculator');
const PaintCalculator = lazy(() => import('@/calculators/ConstructionCalculators'), 'PaintCalculator');
const TileCalculator = lazy(() => import('@/calculators/ConstructionCalculators'), 'TileCalculator');
const BrickCalculator = lazy(() => import('@/calculators/ConstructionCalculators'), 'BrickCalculator');
const RoofCalculator = lazy(() => import('@/calculators/ConstructionCalculators'), 'RoofCalculator');
const FlooringCalculator = lazy(() => import('@/calculators/ConstructionCalculators'), 'FlooringCalculator');
const RebarCalculator = lazy(() => import('@/calculators/ConstructionCalculators'), 'RebarCalculator');
const StaircaseCalculator = lazy(() => import('@/calculators/ConstructionCalculators'), 'StaircaseCalculator');
const SandAggregateCalc = lazy(() => import('@/calculators/ConstructionCalculators'), 'SandAggregateCalc');
const RoomAreaCalc = lazy(() => import('@/calculators/ConstructionCalculators'), 'RoomAreaCalc');

// Health & Fitness calculators
const CalorieCalc = lazy(() => import('@/calculators/HealthFitnessCalculators'), 'CalorieCalc');
const MacroCalc = lazy(() => import('@/calculators/HealthFitnessCalculators'), 'MacroCalc');
const OneRepMaxCalc = lazy(() => import('@/calculators/HealthFitnessCalculators'), 'OneRepMaxCalc');
const RunningPaceCalc = lazy(() => import('@/calculators/HealthFitnessCalculators'), 'RunningPaceCalc');
const WaterIntakeCalc = lazy(() => import('@/calculators/HealthFitnessCalculators'), 'WaterIntakeCalc');
const BodyFatCalc = lazy(() => import('@/calculators/HealthFitnessCalculators'), 'BodyFatCalc');
const PregnancyCalc = lazy(() => import('@/calculators/HealthFitnessCalculators'), 'PregnancyCalc');
const VO2MaxCalc = lazy(() => import('@/calculators/HealthFitnessCalculators'), 'VO2MaxCalc');
const HeartRateZonesCalc = lazy(() => import('@/calculators/HealthFitnessCalculators'), 'HeartRateZonesCalc');
const SleepCycleCalc = lazy(() => import('@/calculators/HealthFitnessCalculators'), 'SleepCycleCalc');

// Engineering Standards (searchable data tables)
const OringTable = lazy(() => import('@/calculators/EngineeringTables'), 'OringTable');
const BoltTable = lazy(() => import('@/calculators/EngineeringTables'), 'BoltTable');
const PipeTable = lazy(() => import('@/calculators/EngineeringTables'), 'PipeTable');
const WireGaugeTable = lazy(() => import('@/calculators/EngineeringTables'), 'WireGaugeTable');
const SheetMetalTable = lazy(() => import('@/calculators/EngineeringTables'), 'SheetMetalTable');
const BearingTable = lazy(() => import('@/calculators/EngineeringTables'), 'BearingTable');
const DrillBitTable = lazy(() => import('@/calculators/EngineeringTables'), 'DrillBitTable');

// Medical Reference
const NeedleTable = lazy(() => import('@/calculators/MedicalReference'), 'NeedleTable');
const IVCannulaTable = lazy(() => import('@/calculators/MedicalReference'), 'IVCannulaTable');
const CatheterTable = lazy(() => import('@/calculators/MedicalReference'), 'CatheterTable');
const SutureTable = lazy(() => import('@/calculators/MedicalReference'), 'SutureTable');
const ETTubeTable = lazy(() => import('@/calculators/MedicalReference'), 'ETTubeTable');
const BPReferenceTable = lazy(() => import('@/calculators/MedicalReference'), 'BPReferenceTable');
const DrugDosageCalc = lazy(() => import('@/calculators/MedicalReference'), 'DrugDosageCalc');
const MedicalUnitConverter = lazy(() => import('@/calculators/MedicalReference'), 'MedicalUnitConverter');

// Text & Numbers
const TextCaseConverter = lazy(() => import('@/calculators/TextNumbersTools'), 'TextCaseConverter');
const TextAnalysis = lazy(() => import('@/calculators/TextNumbersTools'), 'TextAnalysis');
const NumberToWords = lazy(() => import('@/calculators/TextNumbersTools'), 'NumberToWords');
const HashGenerator = lazy(() => import('@/calculators/TextNumbersTools'), 'HashGenerator');

const CALC_MAP = {
  'scientific': ScientificCalculator,
  'equation-solver': EquationSolver,
  'matrix': MatrixCalculator,
  'complex-number': ComplexNumberCalc,
  'base-converter': BaseConverter,
  'prime-factorization': PrimeFactorization,
  'derivative': DerivativeCalc,
  'integral': IntegralCalc,
  'limit': LimitCalc,
  'series': SeriesCalc,
  'function-grapher': FunctionGrapher,
  'kinematics': KinematicsCalc,
  'newtons-laws': NewtonsLawsCalc,
  'energy-work': EnergyWorkCalc,
  'electromagnetism': ElectromagnetismCalc,
  'optics': OpticsCalc,
  'thermodynamics': ThermodynamicsCalc,
  'waves-sound': WavesSoundCalc,
  'relativity': RelativityCalc,
  'molar-mass': MolarMassCalc,
  'stoichiometry': StoichiometryCalc,
  'ph-titration': PhTitrationCalc,
  'gas-laws': GasLawsCalc,
  'electrochemistry': ElectrochemistryCalc,
  'nuclear': NuclearCalc,
  'descriptive-stats': DescriptiveStatsCalc,
  'probability': ProbabilityCalc,
  'hypothesis-testing': HypothesisTestingCalc,
  'regression': RegressionCalc,
  'distribution-tables': DistributionTablesCalc,
  'unit-converter': UnitConverterFull,
  'currency': UnitConverterFull,
  'sig-figs': SigFigsCalc,
  'vector': VectorCalc,
  'fluid-mechanics': FluidMechanicsCalc,
  'electrical-circuits': ElectricalCircuitsCalc,
  'genetics': GeneticsCalc,
  'population-growth': PopulationGrowthCalc,
  'body-metrics': BodyMetricsCalc,
  'astronomy': AstronomyCalc,
  'geoscience': GeoscienceCalc,

  // Aliases — match short IDs used in slugs.js so routes resolve consistently
  'eq-solver': EquationSolver,
  'complex': ComplexNumberCalc,
  'base': BaseConverter,
  'prime': PrimeFactorization,
  'grapher': FunctionGrapher,
  'energy': EnergyWorkCalc,
  'waves': WavesSoundCalc,
  'ph': PhTitrationCalc,
  'descriptive': DescriptiveStatsCalc,
  'hypothesis': HypothesisTestingCalc,
  'distribution': DistributionTablesCalc,
  'population': PopulationGrowthCalc,
  'circuits': ElectricalCircuitsCalc,
  'hydraulics': FluidMechanicsCalc,

  // Finance
  'emi': EMICalculator,
  'mortgage': MortgageCalculator,
  'compound-interest': CompoundInterestCalc,
  'sip': SIPCalculator,
  'gst': GSTCalculator,
  'salary': SalaryCalculator,
  'discount': DiscountCalculator,
  'profit-loss': ProfitLossCalc,
  'roi': ROICalculator,
  'tip': TipCalculator,
  'ppf': PPFCalculator,
  'inflation': InflationCalculator,

  // Construction
  'concrete': ConcreteCalculator,
  'paint': PaintCalculator,
  'tile': TileCalculator,
  'brick': BrickCalculator,
  'roof': RoofCalculator,
  'flooring': FlooringCalculator,
  'rebar': RebarCalculator,
  'staircase': StaircaseCalculator,
  'sand-aggregate': SandAggregateCalc,
  'room-area': RoomAreaCalc,

  // Health & Fitness
  'calorie': CalorieCalc,
  'macro': MacroCalc,
  'one-rep-max': OneRepMaxCalc,
  'running-pace': RunningPaceCalc,
  'water-intake': WaterIntakeCalc,
  'body-fat': BodyFatCalc,
  'pregnancy': PregnancyCalc,
  'vo2max': VO2MaxCalc,
  'heart-rate': HeartRateZonesCalc,
  'sleep-cycle': SleepCycleCalc,

  // Engineering Standards
  'oring': OringTable,
  'bolt': BoltTable,
  'pipe': PipeTable,
  'wire-gauge': WireGaugeTable,
  'sheet-metal': SheetMetalTable,
  'bearing': BearingTable,
  'drill-bit': DrillBitTable,

  // Medical Reference
  'needle': NeedleTable,
  'iv-cannula': IVCannulaTable,
  'catheter': CatheterTable,
  'suture': SutureTable,
  'et-tube': ETTubeTable,
  'bp-reference': BPReferenceTable,
  'drug-dosage': DrugDosageCalc,
  'medical-converter': MedicalUnitConverter,

  // Text & Numbers
  'text-case': TextCaseConverter,
  'text-analysis': TextAnalysis,
  'number-to-words': NumberToWords,
  'hash-generator': HashGenerator,
};

// Loading skeleton for code-split calculators
function CalcSkeleton() {
  return (
    <div className="max-w-4xl mx-auto" data-testid="calc-loading-skeleton" role="status" aria-label="Loading calculator">
      <div className="animate-pulse">
        <div className="h-10 bg-muted rounded-lg w-1/3 mb-3" />
        <div className="h-5 bg-muted rounded w-2/3 mb-6" />
        <div className="flex gap-2 mb-4">
          <div className="h-8 w-24 bg-muted rounded-md" />
          <div className="h-8 w-24 bg-muted rounded-md" />
          <div className="h-8 w-20 bg-muted rounded-md" />
        </div>
        <div className="h-72 bg-muted rounded-xl" />
      </div>
    </div>
  );
}

const MOBILE_TABS = [
  { id: 'home',         icon: Home,     label: 'Home',   kind: 'home' },
  { id: 'finance',      icon: Wallet,   label: 'Finance', kind: 'category' },
  { id: 'construction', icon: Building2, label: 'Build',  kind: 'category' },
  { id: 'health-fitness', icon: Heart,  label: 'Health', kind: 'category' },
  { id: 'more',         icon: Menu,     label: 'More',   kind: 'drawer' },
];

// ── Route components (path-based) ──
function CalcRoute({ shellApi }) {
  const { slug } = useParams();
  const calcId = idFromSlug(slug);
  const ActiveComponent = calcId ? CALC_MAP[calcId] : null;
  const seo = calcId ? getSeoForCalc(calcId) : null;

  if (!calcId || !ActiveComponent) {
    return <NotFound />;
  }
  return (
    <>
      <SEO title={seo.title} description={seo.description} path={`/${slug}`} />
      <Suspense fallback={<CalcSkeleton />}>
        <nav className="cg-breadcrumb" aria-label="Breadcrumb"><Link to="/">Home</Link><span aria-hidden="true">/</span><Link to="/sitemap/">Calculators</Link><span aria-hidden="true">/</span><span>{getCalculatorById(calcId)?.name}</span></nav>
        <ActiveComponent
          addToHistory={shellApi.addToHistory}
          history={shellApi.history}
          initialParams={shellApi.initialParams}
          calcId={calcId}
          showHistory={shellApi.showHistory}
          onToggleHistory={shellApi.toggleHistory}
        />
        <CalculatorContent calcId={calcId} />
      </Suspense>
    </>
  );
}

function CategoryRoute() {
  const { slug } = useParams();
  const categoryId = categoryIdFromSlug(slug);
  const cat = categoryId ? getCategoryById(categoryId) : null;
  const navigate = useNavigate();
  if (!cat) return <NotFound />;
  return (
    <>
      <SEO
        title={`${cat.name} — ${cat.calculators.length} Free Calculators | CalcGlobe`}
        description={cat.description || `Browse ${cat.calculators.length} free ${cat.name.toLowerCase()} calculators on CalcGlobe.`}
        path={`/category/${slug}`}
      />
      <CategoryPage
        categoryId={categoryId}
        onSelectCalc={(id) => navigate('/' + slugFor(id))}
        onGoHome={() => navigate('/')}
      />
    </>
  );
}

function HomeRoute() {
  const navigate = useNavigate();
  return (
    <>
      <SEO
        title="CalcGlobe — 80+ Free Calculators: Finance, Construction, Health, Science, Engineering & Medical"
        description="One place for every calculation. EMI, mortgage, SIP, concrete, paint, TDEE, scientific, physics, chemistry, plus engineering & medical reference. Free forever, no signup."
        path="/"
      />
      <HomePage
        onSelectCalc={(id) => navigate('/' + slugFor(id))}
        onSelectCategory={(id) => navigate('/category/' + categorySlugFor(id))}
      />
    </>
  );
}

function AboutRoute() {
  return (
    <>
      <SEO title="About CalcGlobe — Free Scientific & Engineering Calculator" description="CalcGlobe is the free all-in-one calculator platform with 80+ tools for students, engineers, doctors, builders and finance professionals." path="/about" />
      <AboutPage />
    </>
  );
}

function ContactRoute() {
  return (
    <>
      <SEO title="Contact CalcGlobe — Report a Bug or Suggest a Calculator" description="Get in touch with the CalcGlobe team. Report a bug, suggest a new calculator, or ask a general question." path="/contact" />
      <ContactPage />
    </>
  );
}

function SitemapRoute() {
  return (
    <>
      <SEO title="Sitemap | CalcGlobe" description="Full sitemap of CalcGlobe — browse all 90+ free calculators by category." path="/sitemap" />
      <SitemapPage />
    </>
  );
}

function BlogListRoute() {
  return (
    <>
      <SEO title="Blog | CalcGlobe — Calculator Guides & Worked Examples" description="Practical guides on EMI, SIP, O-rings, IV cannulas, calorie targets and more. Every article includes a working calculator." path="/blog" />
      <BlogListPage />
    </>
  );
}

function BlogPostRoute() {
  const { slug } = useParams();
  // We rely on BlogPostPage to render its own SEO via the post data
  // but still emit a generic title-level SEO here for crawlers.
  return (
    <>
      <BlogSeoWrapper slug={slug} />
      <BlogPostPage />
    </>
  );
}

function BlogSeoWrapper({ slug }) {
  const post = blogPostBySlug[slug];
  if (!post) return <SEO title="Article not found | CalcGlobe" description="The blog article you requested does not exist." path={`/blog/${slug}`} />;
  return <SEO title={`${post.title} | CalcGlobe Blog`} description={post.excerpt} path={`/blog/${post.slug}`} />;
}

function LegalRoute({ slug }) {
  const doc = legalDocs[slug];
  const title = doc ? `${doc.title} | CalcGlobe` : 'Page not found | CalcGlobe';
  const description = doc?.description || 'Legal pages for CalcGlobe.';
  return (
    <>
      <SEO title={title} description={description} path={`/${slug}`} />
      <LegalPage slug={slug} />
    </>
  );
}

function NotFound() {
  return (
    <>
      <SEO title="Page Not Found | CalcGlobe" description="The page you are looking for could not be found." path="/" />
      <div className="max-w-2xl mx-auto py-16 text-center" data-testid="not-found">
        <h1 className="font-chivo text-4xl font-bold mb-2">404</h1>
        <p className="text-muted-foreground mb-6">This page or calculator does not exist.</p>
        <a href="/" className="text-[#1A8FE3] hover:underline">← Back to all calculators</a>
      </div>
    </>
  );
}

// ── Hash → path redirect (back-compat for old shared links like #emi or #cat/finance) ──
function HashRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    const handle = () => {
      const hash = window.location.hash.slice(1);
      if (!hash) return;
      const [head, query] = hash.split('?');
      let target = null;
      if (head.startsWith('cat/')) {
        target = '/category/' + categorySlugFor(head.slice(4));
      } else if (head === 'about') {
        target = '/about';
      } else if (SLUG_BY_ID[head]) {
        target = '/' + SLUG_BY_ID[head];
      }
      if (target) {
        if (query) target += '?' + query;
        // Clear hash and navigate to clean path
        window.location.hash = '';
        navigate(target, { replace: true });
      }
    };
    handle(); // run once on mount
    window.addEventListener('hashchange', handle);
    return () => window.removeEventListener('hashchange', handle);
  }, [navigate]);
  return null;
}

function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [initialParams, setInitialParams] = useState({});
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem('calcglobe-history') || '[]'); }
    catch { return []; }
  });

  // Parse query params from URL into initialParams whenever the path changes
  useEffect(() => {
    const params = Object.fromEntries(new URLSearchParams(location.search));
    setInitialParams(params);
  }, [location.search]);

  // Initialise Google Consent Mode v2 once at boot; grant if user has previously accepted.
  useEffect(() => {
    initConsentMode();
    if (hasAcceptedCookies()) grantAnalyticsConsent();
  }, []);

  // Fire a GA4 page_view on every SPA navigation (after a tick so document.title has updated).
  useEffect(() => {
    const id = setTimeout(() => trackPageView(location.pathname + location.search), 50);
    return () => clearTimeout(id);
  }, [location.pathname, location.search]);

  // Determine current view from path (for active state on bottom tabs)
  const path = location.pathname;
  const activeCategorySlug = path.startsWith('/category/') ? path.slice(10) : null;
  const activeCategoryId = activeCategorySlug ? categoryIdFromSlug(activeCategorySlug) : null;
  const activeCalcSlug = path !== '/' && !path.startsWith('/category/') && path !== '/about' ? path.slice(1) : null;
  const activeCalcId = activeCalcSlug ? idFromSlug(activeCalcSlug) : null;
  const isHome = path === '/';

  const addToHistory = useCallback((entry) => {
    setHistory(prev => {
      const next = [{ ...entry, id: Date.now(), timestamp: new Date().toISOString() }, ...prev].slice(0, 20);
      try { localStorage.setItem('calcglobe-history', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
    try { localStorage.removeItem('calcglobe-history'); } catch {}
  }, []);

  const navigateToCalc = useCallback((calcId) => {
    navigate('/' + slugFor(calcId));
    setSidebarOpen(false);
  }, [navigate]);

  const navigateToCategory = useCallback((categoryId) => {
    navigate('/category/' + categorySlugFor(categoryId));
    setSidebarOpen(false);
    window.scrollTo({ top: 0 });
  }, [navigate]);

  const goHome = useCallback(() => {
    navigate('/');
    setSidebarOpen(false);
  }, [navigate]);

  const reuseHistory = useCallback((entry) => {
    if (entry.calcId && CALC_MAP[entry.calcId]) {
      navigateToCalc(entry.calcId);
    }
    setShowHistory(false);
  }, [navigateToCalc]);

  const shellApi = {
    addToHistory,
    history,
    initialParams,
    showHistory,
    toggleHistory: () => setShowHistory(h => !h),
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <HashRedirect />
      <Sidebar
        activeCalc={activeCalcId}
        onSelectCalc={navigateToCalc}
        onSelectCategory={navigateToCategory}
        onGoHome={goHome}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="min-h-screen flex flex-col">
        <Navbar onToggleSidebar={() => setSidebarOpen(o => !o)} />

        <main className="cg-main flex-1" role="main">
          <Routes>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/about" element={<AboutRoute />} />
            <Route path="/contact" element={<ContactRoute />} />
            <Route path="/sitemap" element={<SitemapRoute />} />
            <Route path="/blog" element={<BlogListRoute />} />
            <Route path="/blog/:slug" element={<BlogPostRoute />} />
            <Route path="/privacy-policy" element={<LegalRoute slug="privacy-policy" />} />
            <Route path="/terms" element={<LegalRoute slug="terms" />} />
            <Route path="/terms-of-use" element={<LegalRoute slug="terms" />} />
            <Route path="/disclaimer" element={<LegalRoute slug="disclaimer" />} />
            <Route path="/cookie-policy" element={<LegalRoute slug="cookie-policy" />} />
            <Route path="/editorial-standards" element={<LegalRoute slug="editorial-standards" />} />
            <Route path="/category/:slug" element={<CategoryRoute />} />
            <Route path="/:slug" element={<CalcRoute shellApi={shellApi} />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>

        {/* Footer */}
        <footer className="border-t bg-muted/30 px-4 md:px-6 pt-10 pb-24 md:pb-10" data-testid="footer">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10">
              {/* Column 1 — Brand */}
              <div className="col-span-2 md:col-span-1" data-testid="footer-col-brand">
                <Link to="/" onClick={goHome} className="inline-flex items-center gap-2.5 mb-3" aria-label="CalcGlobe home">
                  <GlobeLogo size={40} testid="footer-globe-logo" />
                  <span className="font-chivo text-base font-bold tracking-tight">
                    <span className="text-foreground">Calc</span><span className="text-[#1A8FE3]">Globe</span>
                  </span>
                </Link>
                <p className="text-xs text-muted-foreground leading-relaxed mb-3 max-w-[260px]">
                  Every calculation. Every corner of science. 90+ free, fast, professional calculators — no signup, no ads in the way.
                </p>
                <p className="text-[11px] text-muted-foreground/70">
                  &copy; 2026 CalcGlobe — Free forever.
                </p>
              </div>

              {/* Column 2 — Calculators */}
              <div data-testid="footer-col-calculators">
                <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-foreground mb-3">Calculators</h3>
                <ul className="space-y-2 text-xs text-muted-foreground">
                  <li><button onClick={() => navigate('/category/finance-calculators')} className="hover:text-foreground transition-colors" data-testid="footer-cat-finance">Finance &amp; Money</button></li>
                  <li><button onClick={() => navigate('/category/construction-calculators')} className="hover:text-foreground transition-colors" data-testid="footer-cat-construction">Construction</button></li>
                  <li><button onClick={() => navigate('/category/health-fitness-calculators')} className="hover:text-foreground transition-colors" data-testid="footer-cat-health">Health &amp; Fitness</button></li>
                  <li><button onClick={() => navigate('/category/engineering-standards')} className="hover:text-foreground transition-colors" data-testid="footer-cat-engineering">Engineering</button></li>
                  <li><button onClick={() => navigate('/category/medical-reference')} className="hover:text-foreground transition-colors" data-testid="footer-cat-medical">Medical Reference</button></li>
                  <li><button onClick={() => navigate('/unit-converter')} className="hover:text-foreground transition-colors" data-testid="footer-cat-unit">Unit Converter</button></li>
                </ul>
              </div>

              {/* Column 3 — Resources */}
              <div data-testid="footer-col-resources">
                <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-foreground mb-3">Resources</h3>
                <ul className="space-y-2 text-xs text-muted-foreground">
                  <li><button onClick={goHome} className="hover:text-foreground transition-colors" data-testid="footer-home">Home</button></li>
                  <li><button onClick={() => navigate('/blog')} className="hover:text-foreground transition-colors" data-testid="footer-blog">Blog</button></li>
                  <li><button onClick={() => navigate('/about')} className="hover:text-foreground transition-colors" data-testid="footer-about">About</button></li>
                  <li><button onClick={() => navigate('/contact')} className="hover:text-foreground transition-colors" data-testid="footer-contact">Contact</button></li>
                  <li><button onClick={() => navigate('/sitemap')} className="hover:text-foreground transition-colors" data-testid="footer-sitemap">Sitemap</button></li>
                </ul>
              </div>

              {/* Column 4 — Legal */}
              <div data-testid="footer-col-legal">
                <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-foreground mb-3">Legal</h3>
                <ul className="space-y-2 text-xs text-muted-foreground">
                  <li><button onClick={() => navigate('/privacy-policy')} className="hover:text-foreground transition-colors" data-testid="footer-privacy">Privacy Policy</button></li>
                  <li><button onClick={() => navigate('/terms')} className="hover:text-foreground transition-colors" data-testid="footer-terms">Terms of Use</button></li>
                  <li><button onClick={() => navigate('/disclaimer')} className="hover:text-foreground transition-colors" data-testid="footer-disclaimer">Disclaimer</button></li>
                  <li><button onClick={() => navigate('/cookie-policy')} className="hover:text-foreground transition-colors" data-testid="footer-cookie">Cookie Policy</button></li>
                  <li><button onClick={() => window.dispatchEvent(new Event('calcglobe:open-cookie-banner'))} className="hover:text-foreground transition-colors" data-testid="footer-manage-cookies">Manage cookies</button></li>
                </ul>
              </div>
            </div>

            {/* Footer bottom bar */}
            <div className="mt-8 pt-5 border-t border-border/50 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-muted-foreground/80">
              <span>Made for students, professionals, builders &amp; everyone who needs an answer fast.</span>
              <span>v2.0 · Last updated April 2026</span>
            </div>
          </div>
        </footer>
      </div>

      {/* History Panel */}
      {showHistory && (
        <HistoryPanel
          history={history}
          onReuse={reuseHistory}
          onClose={() => setShowHistory(false)}
          onClear={clearHistory}
        />
      )}

      {/* Mobile bottom nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-lg border-t z-50 px-1 py-1.5" data-testid="mobile-nav">
        <div className="flex justify-around">
          {MOBILE_TABS.map(tab => {
            const Icon = tab.icon;
            const isActive =
              (tab.kind === 'home' && isHome) ||
              (tab.kind === 'category' && activeCategoryId === tab.id);
            return (
              <button
                key={tab.id}
                onClick={() => {
                  if (tab.kind === 'home') goHome();
                  else if (tab.kind === 'category') navigateToCategory(tab.id);
                  else if (tab.kind === 'drawer') setSidebarOpen(true);
                }}
                className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-md transition-colors ${isActive ? 'text-[#1A8FE3]' : 'text-muted-foreground'}`}
                data-testid={`mobile-tab-${tab.id}`}
                aria-label={tab.label}
              >
                <Icon className="w-4 h-4" />
                <span className="text-[10px]">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <BrowserRouter>
          <AppShell />
          <CookieConsent />
        </BrowserRouter>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
